import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import { PushProvider } from "./contexts/PushContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import { Toaster } from "./components/ui/sonner";
import PushPermissionBanner from "./components/PushPermissionBanner";
import SeoHead from "./components/SeoHead";
import CookieConsentBanner from "./components/CookieConsentBanner";

import LeendrHeader from "./components/LeendrHeader";
// Eager: critical-path pages (homepage + auth callback)
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";

// Lazy: all secondary routes — split into separate chunks for first-paint speed
const LoginPage              = lazy(() => import("./pages/LoginPage"));
const RegisterPage           = lazy(() => import("./pages/RegisterPage"));
const VerifyEmailSentPage    = lazy(() => import("./pages/VerifyEmailSentPage"));
const VerifyEmailPage        = lazy(() => import("./pages/VerifyEmailPage"));
const VerifyPhonePage        = lazy(() => import("./pages/VerifyPhonePage"));
const ListingsPage           = lazy(() => import("./pages/ListingsPage"));
const ListingDetailPage      = lazy(() => import("./pages/ListingDetailPage"));
const CreateListingPage      = lazy(() => import("./pages/CreateListingPage"));
const DashboardPage          = lazy(() => import("./pages/DashboardPage"));
const PaymentSuccessPage     = lazy(() => import("./pages/PaymentSuccessPage"));
const ProfilePage            = lazy(() => import("./pages/ProfilePage"));
const MessagesPage           = lazy(() => import("./pages/MessagesPage"));
const ReferralPage           = lazy(() => import("./pages/ReferralPage"));
const AdminDashboardPage     = lazy(() => import("./pages/AdminDashboardPage"));
const AdminBirdPage          = lazy(() => import("./pages/AdminBirdPage"));
const AdminAnalyticsPage     = lazy(() => import("./pages/AdminAnalyticsPage"));
const AdminWiderrufePage     = lazy(() => import("./pages/AdminWiderrufePage"));
const AdminIntegrationsPage  = lazy(() => import("./pages/AdminIntegrationsPage"));
const UnsubscribePage        = lazy(() => import("./pages/UnsubscribePage"));
const EinfachLoginPage       = lazy(() => import("./pages/EinfachLoginPage"));
const MagicLinkPage          = lazy(() => import("./pages/MagicLinkPage"));
const FavoritesPage          = lazy(() => import("./pages/FavoritesPage"));
const PriceAlertsPage        = lazy(() => import("./pages/PriceAlertsPage"));
const FAQPage                = lazy(() => import("./pages/FAQPage"));
const DatenschutzPage        = lazy(() => import("./pages/DatenschutzPage"));
const ImpressumPage          = lazy(() => import("./pages/ImpressumPage"));
const AGBPage                = lazy(() => import("./pages/AGBPage"));
const WiderrufPage           = lazy(() => import("./pages/WiderrufPage"));
const CityPage               = lazy(() => import("./pages/CityPage"));
const CityIndexPage          = lazy(() => import("./pages/CityIndexPage"));
const ForgotPasswordPage     = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage      = lazy(() => import("./pages/ResetPasswordPage"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EEEDFE' }}>
      <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '3px solid #EEEDFE', borderTopColor: '#534AB7' }} />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EEEDFE' }}>
        <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '3px solid #EEEDFE', borderTopColor: '#534AB7' }} />
      </div>
    );
  }
  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Pages with NO header (own layout)
  const noHeaderPaths = ['/', '/login', '/register', '/verify-email-sent', '/verify-email', '/verify-phone',
    '/faq', '/datenschutz', '/impressum', '/agb', '/widerruf', '/miete',
    '/forgot-password', '/reset-password'];
  const isCityPage = location.pathname.startsWith('/miete/');
  const showHeader = !noHeaderPaths.includes(location.pathname) && !isCityPage;

  return (
    <>
      {showHeader && <LeendrHeader />}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<EinfachLoginPage />} />
        <Route path="/login/passwort" element={<LoginPage />} />
        <Route path="/register" element={<EinfachLoginPage />} />
        <Route path="/register/passwort" element={<RegisterPage />} />
        <Route path="/verify-email-sent" element={<VerifyEmailSentPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/verify-phone" element={<ProtectedRoute><VerifyPhonePage /></ProtectedRoute>} />
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/listings/:id" element={<ListingDetailPage />} />
        <Route path="/create-listing" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/bird" element={<ProtectedRoute><AdminBirdPage /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>} />
        <Route path="/admin/widerrufe" element={<ProtectedRoute><AdminWiderrufePage /></ProtectedRoute>} />
        <Route path="/admin/integrations" element={<ProtectedRoute><AdminIntegrationsPage /></ProtectedRoute>} />
        <Route path="/u/:short" element={<UnsubscribePage />} />
        <Route path="/anmelden" element={<EinfachLoginPage />} />
        <Route path="/anmelden/link" element={<MagicLinkPage />} />
        <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
        <Route path="/price-alerts" element={<ProtectedRoute><PriceAlertsPage /></ProtectedRoute>} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/agb" element={<AGBPage />} />
        <Route path="/widerruf" element={<WiderrufPage />} />
        <Route path="/miete" element={<CityIndexPage />} />
        <Route path="/miete/:city" element={<CityPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <CookieConsentProvider>
            <PushProvider>
              <SeoHead />
              <AppRouter />
              <PushPermissionBanner />
              <CookieConsentBanner />
              <Toaster position="top-right" richColors />
            </PushProvider>
          </CookieConsentProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
